import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import type { ChartData, ChartOptions } from 'chart.js'
import { WeeklySalesBucket } from '../../types/dashboard'
import { fmt } from '../../lib/dashboardHelpers'
import { useDashboardLayoutStore, type WidgetSettings } from '../../stores/dashboardLayoutStore'
import { useChartColors } from '../../hooks/useChartColors'
import { useThemeStore } from '../../stores/themeStore'

interface Props {
    weekly: WeeklySalesBucket[]
    selectedDate: string // YYYY-MM-DD
    settings?: WidgetSettings
    onDateSelect?: (date: string) => void
}

export default function WeeklySalesChart({ weekly, selectedDate, settings, onDateSelect }: Props) {
    const primaryComparison = useDashboardLayoutStore(s => s.primaryComparison)
    const colors = useChartColors()
    const colorMode = useThemeStore(s => s.getEffectiveColorMode())
    const textColor = colorMode === 'dark' ? '#94a3b8' : '#475569'
    const gridColor = colorMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'

    const chartData: ChartData<'bar' | 'line'> = useMemo(() => {
        if (!weekly || weekly.length === 0) return { labels: [], datasets: [] }

        // Determine if selected date is in the current week range represented by the data
        // For simplicity, we can just use the C# returned day of week strings.
        // We need to reorder to Monday-Sunday
        const dowOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

        // Map data to ordered days
        const orderedData = dowOrder.map(dayName => {
            const match = weekly.find(w => w.dayOfWeek === dayName)
            return match || { dayOfWeek: dayName, sales: 0, discount: 0, lastWeekSales: 0, lastMonthSales: 0, lastYearSales: 0 }
        })

        // Identify selected day of week to highlight
        const selectedObj = new Date(selectedDate + 'T12:00:00')
        const selectedDow = selectedObj.toLocaleDateString('en-US', { weekday: 'long' })

        // Find Monday of the selected week
        const dayOfWeek = selectedObj.getDay() // 0 = Sunday, 1 = Monday
        const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
        const mondayObj = new Date(selectedObj)
        mondayObj.setDate(mondayObj.getDate() - diffToMonday)

        const todayObj = new Date()

        const labels = orderedData.map((d, index) => {
            const colDate = new Date(mondayObj)
            colDate.setDate(colDate.getDate() + index)

            if (colDate.toDateString() === todayObj.toDateString()) {
                return 'Today'
            }
            return d.dayOfWeek.substring(0, 3)
        })

        const showDiscounts = settings?.showDiscounts !== false
        // Chart reads purely from widget settings (dashboard Compare To sets these)
        const showLastWeek = settings?.showLastWeek !== false
        const showLastMonth = settings?.showLastMonth === true
        const showLastYear = settings?.showLastYear !== false

        const currentSales = orderedData.map(d => d.sales)
        const currentDiscounts = orderedData.map(d => d.discount || 0)
        const lastWeekSales = orderedData.map(d => d.lastWeekSales)
        const lastMonthSales = orderedData.map(d => d.lastMonthSales)
        const lastYearSales = orderedData.map(d => d.lastYearSales)

        // Dynamic coloring based on selected day. 
        // We use primary for selected day and secondary for normal days.
        const bgColors = orderedData.map(d =>
            d.dayOfWeek === selectedDow
                ? colors.primary  // Selected
                : colors.secondary  // Other days
        )

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const datasets: any[] = [
            {
                type: 'bar' as const,
                label: showDiscounts ? 'Net Sales' : 'This Week',
                data: currentSales,
                backgroundColor: bgColors,
                borderRadius: 4,
                stack: 'thisWeek',
                order: 2
            }
        ]

        if (showDiscounts) {
            datasets.push({
                type: 'bar' as const,
                label: 'Discounts',
                data: currentDiscounts,
                backgroundColor: colors.primarySubtle,
                borderRadius: 4,
                stack: 'thisWeek',
                order: 2
            })
        }

        datasets.push({
            type: 'line' as const,
            label: 'Last Week',
            data: lastWeekSales,
            borderColor: colors.comp1,
            borderWidth: 2,
            borderDash: [4, 4],
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false,
            tension: 0.3,
            hidden: !showLastWeek,
            order: 1
        })

        datasets.push({
            type: 'line' as const,
            label: 'Last Month',
            data: lastMonthSales,
            borderColor: 'rgba(168, 85, 247, 0.6)',
            borderWidth: 2,
            borderDash: [6, 3],
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false,
            tension: 0.3,
            hidden: !showLastMonth,
            order: 1
        })
        
        datasets.push({
            type: 'line' as const,
            label: 'Last Year',
            data: lastYearSales,
            borderColor: colors.comp2,
            borderWidth: 2,
            borderDash: [2, 2],
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false,
            tension: 0.3,
            hidden: !showLastYear,
            order: 0
        })

        return {
            labels,
            datasets
        }
    }, [weekly, selectedDate, primaryComparison, colors, settings])

    const options: ChartOptions<'bar' | 'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        onClick: (_event, elements) => {
            if (elements.length > 0 && onDateSelect) {
                const index = elements[0].index;
                const clickedDate = new Date(selectedDate + 'T12:00:00');
                const dayOfWeek = clickedDate.getDay();
                const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                clickedDate.setDate(clickedDate.getDate() - diffToMonday + index);
                const yyyy = clickedDate.getFullYear();
                const mm = String(clickedDate.getMonth() + 1).padStart(2, '0');
                const dd = String(clickedDate.getDate()).padStart(2, '0');
                onDateSelect(`${yyyy}-${mm}-${dd}`);
            }
        },
        plugins: {
            legend: {
                display: false // We use our custom HTML legend below
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                titleFont: { size: 12 }, bodyFont: { size: 11 },
                callbacks: {
                    label: (cb) => ` ${cb.dataset.label}: ${fmt(cb.raw as number, 'currency')}`
                },
                itemSort: (a, b) => {
                    const getRank = (label: string) => {
                        if (label === 'Net Sales' || label === 'This Week') return 1;
                        if (label === 'Discounts') return 2;
                        if (label === 'Last Year') return 3;
                        if (label === 'Last Month') return 4;
                        if (label === 'Last Week') return 5;
                        return 99;
                    };
                    return getRank(a.dataset.label as string) - getRank(b.dataset.label as string);
                }
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: { display: false },
                ticks: {
                    color: textColor,
                    font: (ctx) => {
                        const label = ctx.chart.data.labels?.[ctx.index]
                        return {
                            size: 11,
                            weight: label === 'Today' ? 'bold' : 'normal'
                        }
                    }
                }
            },
            y: {
                stacked: true,
                beginAtZero: true,
                border: { display: false },
                grid: { color: gridColor },
                ticks: {
                    color: textColor,
                    font: { size: 11 },
                    callback: (value) => fmt(value as number, 'currency'),
                    maxTicksLimit: 6
                }
            }
        }
    }

    // Calculate Week No
    const selectedObj = new Date(selectedDate + 'T12:00:00')
    const startOfYear = new Date(selectedObj.getFullYear(), 0, 1)
    const days = Math.floor((selectedObj.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000))
    const weekNumber = Math.ceil((selectedObj.getDay() + 1 + days) / 7)

    // Format Mon Jan 5, 2026
    const dateFormatted = selectedObj.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
    
    let compDateStr = ''
    if (primaryComparison && primaryComparison !== 'none') {
        const cDate = new Date(selectedObj)
        if (primaryComparison === 'yesterday') cDate.setDate(cDate.getDate() - 1)
        else if (primaryComparison === 'lastWeek') cDate.setDate(cDate.getDate() - 7)
        else if ((primaryComparison as string) === 'lastMonth') cDate.setDate(cDate.getDate() - 28)
        else if (primaryComparison === 'lastYear') cDate.setDate(cDate.getDate() - 364)
        compDateStr = `   vs ${cDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}`
    }

    const legendItems: any[] = []
    
    // Add "Discounts" if visible
    if (settings?.showDiscounts !== false) {
        legendItems.push({ label: 'Net Sales', color: colors.secondary, isRect: true })
        legendItems.push({ label: 'Selected', color: colors.primary, isRect: true })
        legendItems.push({ label: 'Discounts', color: colors.primarySubtle, isRect: true })
    } else {
        legendItems.push({ label: 'This Week', color: colors.secondary, isRect: true })
        legendItems.push({ label: 'Selected', color: colors.primary, isRect: true })
    }

    if (settings?.showLastWeek !== false) {
        legendItems.push({ label: 'Last Week', color: colors.comp1, borderDash: '4px 4px' })
    }
    if (settings?.showLastMonth === true) {
        legendItems.push({ label: 'Last Month', color: 'rgba(168, 85, 247, 0.6)', borderDash: '6px 3px' })
    }
    if (settings?.showLastYear !== false) {
        legendItems.push({ label: 'Last Year', color: colors.comp2, borderDash: '2px 2px' })
    }

    return (
        <div className="chart-panel" style={{ height: '100%' }}>
            <div className="chart-panel-title">
                Week {weekNumber} - {dateFormatted}
                {compDateStr && <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 'normal', whiteSpace: 'pre' }}>{compDateStr}</span>}
            </div>

            <div className="chart-panel-content" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, minHeight: 0 }}>
                    {/* @ts-ignore - chart.js type inference issues with mixed charts */}
                    <Bar data={chartData} options={options} />
                </div>

                {/* Custom Legend at bottom to match HourlyChart's native legend placement roughly */}
                <div style={{
                    display: 'flex', justifyContent: 'center', gap: '1rem',
                    marginTop: '8px', fontSize: '12px', flexWrap: 'wrap',
                    color: textColor
                }}>
                    {legendItems.map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {item.isRect ? (
                                <div style={{
                                    width: '10px', height: '10px',
                                    backgroundColor: item.color,
                                    borderRadius: '2px'
                                }}></div>
                            ) : (
                                <div style={{
                                    width: '14px', height: '0',
                                    borderTop: `2px dashed ${item.color}`,
                                }}></div>
                            )}
                            <span>{item.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
